import { useMemo, useState, useEffect } from "react";

import Select from 'react-select';
import ButtonLoader from './ButtonLoader';
import DatePicker from "react-date-picker";

import useDocumentTypes from "../hooks/useDocumentTypes";
import { toApiDateTime, formatDateToSydneyTimeZone } from "../utils/dateUtils";

const UploadDocumentForm = ({ document, documentType, disabledDocumentTypes = [], isUploading, onSubmit, onCloseClicked }) => {
  const initialDateIssued = document?.dateIssued
    ? new Date(formatDateToSydneyTimeZone(document.dateIssued, 'yyyy-MM-dd'))
    : null;
  const [dateIssued, setDateIssued] = useState(initialDateIssued);

  const initialExpirationDate = document?.expirationDate
    ? new Date(formatDateToSydneyTimeZone(document.expirationDate, 'yyyy-MM-dd'))
    : null;
  const [expirationDate, setExpirationDate] = useState(initialExpirationDate);

  const { data: documentTypesData, isLoading } = useDocumentTypes({ page: 1, perPage: 100 });

  const documentTypes = documentTypesData?.documentTypes;

  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [file, setFile] = useState(null);

  const documentTypeOptions = useMemo(() => {
    return documentTypes
      ?.filter(documentType => !disabledDocumentTypes.includes(documentType._id))
      ?.map(documentType => ({
        value: documentType._id,
        label: documentType.name
      }));
  }, [documentTypes, disabledDocumentTypes]);

  useEffect(() => {
    if (documentType && selectedDocumentType?.value !== documentType._id && documentTypeOptions) {
      setSelectedDocumentType(documentTypeOptions.find(l => l.value === documentType._id));
    }
  }, [documentType, documentTypeOptions, selectedDocumentType]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const resolvedType = selectedDocumentType?.value ?? documentType?._id;

    if (!resolvedType) return;

    const payload = {
      dateIssued: toApiDateTime(dateIssued),
      expirationDate: toApiDateTime(expirationDate),
      type: resolvedType,
      ...(file ? { file } : {})
    };

    onSubmit(payload);
  }

  return (
    <div className="bg-white rounded-lg border border-ase-blue w-full md:w-[600px] overflow-visible">
      <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
        <p>Upload Document</p>

        <p
          onClick={onCloseClicked}
          className="font-bold cursor-pointer text-2xl"
        >
          X
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col w-full overflow-visible"
      >
        <div className="p-4 flex flex-col gap-4">
          <label className='flex flex-col gap-1'>
            <span>Document type</span>

            <Select
              value={selectedDocumentType}
              onChange={setSelectedDocumentType}
              options={documentTypeOptions}
              isDisabled={document || documentType || isLoading}
              required={true}
            />
          </label>

          <div className="flex items-center justify-center gap-4">
            <label className="flex flex-col gap-1 w-full">
              Date issued

              <DatePicker
                value={dateIssued}
                onChange={setDateIssued}
                required={true}
                clearIcon={null}
                format="d/M/yyyy"
                className="h-[46px]"
                maxDetail="month"
                minDetail="year"
              />
            </label>

            <label className="flex flex-col gap-1 w-full">
              Expiry date

              <DatePicker
                value={expirationDate}
                onChange={setExpirationDate}
                minDate={dateIssued || null}
                required={true}
                clearIcon={null}
                format="d/M/yyyy"
                className="h-[46px]"
                maxDetail="month"
                minDetail="year"
              />
            </label>
          </div>

          <div>
            <label
              className="block mb-2 text-sm font-medium"
              htmlFor="file_input"
            >
              Upload file
            </label>

            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              required={!document}
              id="file_input"
              className="block p-4 w-full text-sm text-white rounded-lg cursor-pointer bg-ase-blue focus:outline-none ase-upload-input"
            />
          </div>

          <button
            type="submit"
            className="ase-button-primary h-[48px] w-full flex items-center justify-center"
          >
            {isUploading ? <ButtonLoader /> : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UploadDocumentForm;
